/**
 * React 19 resetea un `<form action={...}>` después de cada envío (también con
 * `useActionState`). Si la acción vuelve con un error, los inputs no
 * controlados quedan vacíos y la persona tiene que tipear todo de nuevo.
 *
 * El patrón (ver signup-form, PR #46): guardar lo enviado en un estado y usarlo
 * como `defaultValue` de cada input. El reset de React vuelve a ese valor.
 *
 * `keepSubmittedValues` envuelve la acción: antes de llamarla, recuerda SOLO
 * los campos listados en `keys` (nunca listar contraseñas ni honeypots).
 */

export type SubmittedValues<K extends string> = Record<K, string>;

/** Valores iniciales vacíos para las `keys` dadas. */
export function emptySubmittedValues<K extends string>(
  keys: readonly K[],
): SubmittedValues<K> {
  const out = {} as SubmittedValues<K>;
  for (const key of keys) out[key] = "";
  return out;
}

/** Lee las `keys` del FormData como texto (un File o un campo ausente → ""). */
export function pickSubmittedValues<K extends string>(
  formData: FormData,
  keys: readonly K[],
): SubmittedValues<K> {
  const out = {} as SubmittedValues<K>;
  for (const key of keys) {
    const value = formData.get(key);
    out[key] = typeof value === "string" ? value : "";
  }
  return out;
}

/**
 * Envuelve una acción de `useActionState` para que, antes de ejecutarla,
 * guarde lo enviado con `remember`. No toca el resultado de la acción.
 */
export function keepSubmittedValues<S, K extends string>(
  keys: readonly K[],
  remember: (values: SubmittedValues<K>) => void,
  action: (prev: S, formData: FormData) => Promise<S>,
): (prev: S, formData: FormData) => Promise<S> {
  return (prev, formData) => {
    remember(pickSubmittedValues(formData, keys));
    return action(prev, formData);
  };
}
